<template>
  <div class="documents">
    <m-create-analyse
      :active="shouldDisplayModal"
      :modal-info="modalInfo"
      @close="closeModalCreateDocument"
      @submitted="updateAllData"
    />

    <c-loader v-if="isLoadingArchive" :active="isLoadingArchive" label="Загружаем архив с документами" height="fixed" />

    <transition name="fade">
      <div
        v-if="!isDataLoading && !noRequiredAnalyses"
        :class="{ 'documents-need-add-hide': shouldHideNeededDocuments }"
        class="documents-need-add"
      >
        <div class="documents-need-add-title">
          <button
            v-if="requiredAnalysesToAdd.length > 0"
            v-b-toggle="'documents-need-add-docs-group-collapse'"
            class="documents-need-add-title-button"
          >
            <svg width="8" height="5" viewBox="0 0 8 5" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M3.53976 4.4727C3.28126 4.21408 3.28126 3.79476 3.53976 3.53614L6.87001 0.204315C7.12851 -0.0543084 7.54762 -0.054308 7.80612 0.204315C8.06462 0.462938 8.06462 0.88225 7.80612 1.14087L4.47587 4.4727C4.21737 4.73132 3.79826 4.73132 3.53976 4.4727Z"
                fill="#0066FF"
              />
              <path
                d="M4.47058 4.4727C4.72908 4.21408 4.72908 3.79476 4.47058 3.53614L1.12999 0.193967C0.871489 -0.064656 0.452377 -0.0646556 0.193876 0.193968C-0.0646252 0.452591 -0.0646252 0.871902 0.193876 1.13053L3.53447 4.4727C3.79297 4.73132 4.21208 4.73132 4.47058 4.4727Z"
                fill="#0066FF"
              />
            </svg>
          </button>
          Необходимо добавить
        </div>
        <b-collapse id="documents-need-add-docs-group-collapse" visible>
          <template v-if="requiredAnalysesToAdd.length > 0">
            <div v-for="group in requiredAnalysesToAdd" :key="group.name" class="documents-need-add-docs-group">
              <div class="documents-need-add-docs-group-title">{{ group.name }}</div>
              <div v-for="analyse in group.items" :key="analyse.id" class="documents-need-add-docs-group-item">
                <c-button
                  v-show="isUserAbleToControlAnalyses"
                  v-b-popover.html="{
                    container: '#app',
                    trigger: 'hover blur',
                    content: 'Добавить анализ',
                    placement: 'top',
                  }"
                  class="documents-need-add-docs-group-item-add"
                  size="small"
                  :icon="{ icon: 'plus', color: 'primary' }"
                  @click="openModalCreateAnalyse(analyse.id)"
                />
                <div class="documents-need-add-docs-group-item-name" :title="analyse.name">{{ analyse.name }}</div>
              </div>
            </div>
          </template>
          <all-documents-added-success-message
            :should-display="displayAllAnalysesAddedMessage"
            message="Все обязательные анализы добавлены"
            @close-action="shouldHideRequiredToAddBlock = false"
          />
        </b-collapse>
      </div>
    </transition>

    <div
      v-if="!isDataLoading"
      class="documents-content"
      :class="{ 'documents-content-not-need-documents-add': shouldExpandTable }"
    >
      <div class="documents-content-header">
        <div class="documents-content-header-buttons" :class="{ 'no-required-analyses': noRequiredAnalyses }">
          <div v-if="!isTableLoading && noRequiredAnalyses" class="no-required-analyses-label">
            Для должности <span>{{ userPosition }}</span> нет обязательных анализов
          </div>
          <c-button
            v-if="!noRequiredAnalyses"
            class="documents-content-header-buttons-download"
            size="small"
            :responsive="true"
            :disabled="!isAbleToDownloadZip"
            icon="download"
            type="with-icon"
            label="Скачать одним архивом"
            @click="downloadAnalysesZip"
          />
          <c-button
            v-if="isUserAbleToControlAnalyses"
            class="documents-content-header-buttons-add-document"
            :class="{ 'no-required-analyses-add-button': noRequiredAnalyses }"
            size="small"
            :responsive="!noRequiredAnalyses"
            :icon="{ icon: 'plus', color: 'primary' }"
            type="with-icon"
            label="Добавить анализ"
            @click="openModalCreateAnalyse(null)"
          />
        </div>
      </div>
      <div class="documents-content-table">
        <medical-documents-table
          v-if="!isTableLoading && !isDataLoading && !noRequiredAnalyses"
          id="actingDocuments"
          ref="table"
          :levels="1"
          :sorted-column="sortedColumn"
          :disabled-columns="[]"
          :data="tableDataMedicalDocuments"
          class="documents-content-table-table"
          empty-records-label="Анализы пока не добавлены"
          @sortedEvent="resortTable"
          @resendAnalyse="resendAnalyse"
          @analyseDeleted="onAnalyseDelete"
        />
        <c-loader
          v-if="isTableLoading"
          class="documents-content-table-loader"
          label="Данные загружаются"
          :active="isTableLoading"
          height="280"
        />
      </div>
    </div>

    <c-loader
      v-if="isDataLoading"
      class="documents-loader"
      label="Данные загружаются"
      :active="isDataLoading"
      height="500"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import moment from "moment";
import Button from "@/components/controls2/Button";
import AllDocumentsAddedSuccessMessage from "./components/all-documents-added-success-message";
import MedicalDocumentsTable from "@/pages/personal-employee/additions/medical-documents-table";
import DocumentsCreateEditDocumentModal from "@/pages/personal-employee/additions/modals/medical-documents-create-edit-modal";
import Loader from "@/components/loader";
import { reformatFetchedTableData } from "../../../helpers/medicalDocumentsFunctions";

const medicalAnalysesTableColumns = [
  {
    id: "analyse_type",
    label: "Анализ",
    fullName: "Анализ",
    type: "string",
    level: 0,
    width: "35%",
    colspan: 1,
    characteristic: "",
  },
  {
    id: "analyse_status",
    label: "Статус",
    fullName: "Статус",
    type: "string",
    level: 0,
    width: "12%",
    colspan: 1,
    characteristic: "",
  },
  {
    id: "analyse_date",
    label: "Дата анализа",
    fullName: "Дата анализа",
    type: "string",
    level: 0,
    width: "15%",
    colspan: 1,
    characteristic: "",
  },
  {
    id: "analyse_expiration",
    label: "Срок годности",
    fullName: "Срок годности",
    type: "string",
    level: 0,
    width: "23%",
    colspan: 1,
    characteristic: "",
  },
];

const sortingColumns = {
  analyse_status: "status",
  analyse_type: "name",
  analyse_date: "date",
  analyse_expiration: "expirationDate",
};

export default {
  name: "medical-documents",
  components: {
    "c-button": Button,
    "m-create-analyse": DocumentsCreateEditDocumentModal,
    "c-loader": Loader,
    AllDocumentsAddedSuccessMessage,
    MedicalDocumentsTable,
  },
  data() {
    return {
      isDataLoading: false,
      isTableLoading: false,
      isLoadingArchive: false,
      shouldHideRequiredToAddBlock: true,
      shouldDisplayModal: false,
      modalInfo: {
        analyseId: null,
        isEdit: false,
      },
      requiredAnalysesToAdd: [],
      sortedColumn: {
        sortBy: "name",
        sortType: "asc",
      },
      tableDataMedicalDocuments: {
        type: "medical-documents",
        columns: [],
        records: [],
        actions: true,
        isGroupByCategory: 1,
        enabledFields: [],
        levels: 1,
        filteredColumns: [],
        activeFilteredColumns: [],
        globalFilters: [],
      },
      isAbleToDownloadZip: false,
    };
  },
  computed: {
    ...mapGetters({
      getAnalyseTypeNameById: "medicalAnalysesForm/getAnalyseTypeNameById",
      isUserAbleToControlAnalyses: "personalEmployee/medicalDocuments/isUserAbleToControlAnalyses",
      userPosition: "personalEmployee/getUserPosition",
    }),
    shouldHideNeededDocuments() {
      return !this.shouldHideRequiredToAddBlock && this.requiredAnalysesToAdd.length === 0;
    },
    displayAllAnalysesAddedMessage() {
      return this.requiredAnalysesToAdd.length === 0;
    },
    noRequiredAnalyses() {
      return !this.requiredAnalysesToAdd.length && !this.tableDataMedicalDocuments.records.length;
    },
    shouldExpandTable() {
      return this.noRequiredAnalyses || (!this.shouldHideRequiredToAddBlock && this.requiredAnalysesToAdd.length === 0);
    },
  },
  watch: {
    sortedColumn: {
      handler(value) {
        this.fetchTableData(value);
      },
      deep: true,
    },
  },
  async created() {
    document.title = "Мед.книжка";

    this.isDataLoading = true;

    await this.$store.dispatch("medicalAnalysesForm/fetchAnalysesTypes");
    this.tableDataMedicalDocuments.columns = medicalAnalysesTableColumns.map((column) => {
      column.isSorted = column.id === this.sortedColumn.sortBy;
      return column;
    });

    this.fetchRequiredAnalysesList();
    this.fetchTableData();
  },
  methods: {
    openModalCreateAnalyse(analyseId) {
      this.modalInfo = {
        analyseId,
      };
      this.shouldDisplayModal = true;
    },
    closeModalCreateDocument() {
      this.modalInfo = {
        analyseId: null,
        isEdit: false,
      };
      this.shouldDisplayModal = false;
    },
    updateAllData() {
      this.isDataLoading = true;
      this.fetchTableData();
      this.fetchRequiredAnalysesList();
    },
    onAnalyseDelete(analyseId, groupId) {
      const categories = this.tableDataMedicalDocuments.records;
      categories.forEach((category, categoryIndex) => {
        if (category.id === groupId) {
          category.records = category.records.filter((record) => record.analysisId !== analyseId);
          if (!category.records.length) {
            categories.splice(categoryIndex, 1);
          }
        }
      });
      this.fetchRequiredAnalysesList();
    },
    downloadAnalysesZip() {
      this.isLoadingArchive = true;
      const workerId = this.$route.params.id;
      this.$store
        .dispatch("personalEmployee/medicalDocuments/getLinkForArchiveOfAnalyses", workerId)
        .then((res) => {
          if (res.success) {
            const urlToArchive = window.location.origin + res.url;
            const link = document.createElement("a");
            link.download = `Документы_${moment().format("DD_MM_YYYY")}.zip`;
            link.href = urlToArchive;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        })
        .catch(() => {
          console.log("При попытке скачать файл произошла ошибка");
        })
        .finally(() => {
          setTimeout(() => {
            this.isLoadingArchive = false;
          }, 300);
        });
    },
    resortTable(sortData = null) {
      const { sortBy, sortType } = sortData;
      if (sortBy && sortType) {
        this.sortedColumn = {
          sortBy: sortingColumns[sortBy],
          sortType,
        };
      }
    },
    resendAnalyse(analyseId) {
      this.modalInfo = {
        analyseId,
        isEdit: true,
      };
      this.shouldDisplayModal = true;
    },
    fetchRequiredAnalysesList() {
      const workerId = this.$route.params.id;
      this.requiredAnalysesToAdd = [];

      this.$store.dispatch("personalEmployee/medicalDocuments/fetchRequiredAnalysesList", workerId).then((response) => {
        const requiredAnalyses = [];
        response.forEach((category) => {
          if (category.analyses.length === 0) {
            return;
          }

          requiredAnalyses.push({
            name: this.getAnalyseTypeNameById(category.analyses[0].type_id),
            items: category.analyses,
          });
        });

        this.requiredAnalysesToAdd = requiredAnalyses;
        this.isDataLoading = false;
      });
    },
    fetchTableData() {
      this.isTableLoading = true;
      const workerId = this.$route.params.id;

      this.tableDataMedicalDocuments.records = [];
      const sortingValue = `${this.sortedColumn.sortType === "asc" ? "" : "-"}${
        this.sortedColumn.sortBy ? this.sortedColumn.sortBy : "name"
      }`;
      this.$store
        .dispatch("personalEmployee/medicalDocuments/fetchAddedAnalysesList", {
          workerId,
          sort: sortingValue,
        })
        .then((response) => {
          response.forEach((category) => {
            if (!category.analyses.length) {
              return;
            }

            let containsExpiredItem = false;
            let containsExpiringSoonItem = false;

            const records = category.analyses.map((record) => {
              const { workerAnalysis } = record;
              if (workerAnalysis.files.length > 0) {
                this.isAbleToDownloadZip = true;
              }
              if (workerAnalysis.expired) {
                containsExpiredItem = true;
              }
              if (workerAnalysis.expireSoon) {
                containsExpiringSoonItem = true;
              }

              return reformatFetchedTableData(record);
            });

            this.tableDataMedicalDocuments.records.push({
              id: category.id,
              records,
              containsExpiredItem,
              containsExpiringSoonItem,
              categoryName: this.getAnalyseTypeNameById(category.analyses[0].type_id),
            });
          });
        })
        .finally(() => {
          this.isTableLoading = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/vars";

.documents {
  display: flex;

  @media (max-width: 480px) {
    margin-top: 30px !important;
  }

  @media screen and (max-width: 1024px) {
    flex-direction: column;
  }

  &-need-add {
    display: flex;
    flex-direction: column;
    max-width: 268px;
    width: 100%;
    margin-bottom: 20px;
    transition: all 0.4s ease-out;

    &-hide {
      opacity: 0;
      max-width: 0;
      max-height: 0 !important;
      max-width: 0 !important;
      margin-bottom: 0;

      & .documents-need-add-title {
        font-size: 0 !important;
        line-height: 0 !important;
        max-width: 0 !important;
      }
    }

    @media screen and (max-width: 1024px) {
      max-width: 100%;
    }

    &-title {
      display: flex;
      align-items: center;
      font-weight: 500;
      font-size: 18px;
      line-height: 22px;

      @media screen and (max-width: 700px) {
        font-size: 15px;
        line-height: 18px;
      }

      &-button {
        display: none;
        margin-right: 12px;
        justify-content: center;
        background: #ffffff;
        align-items: center;
        width: 22px;
        height: 22px;
        border-radius: 50%;
        outline: none;
        border: 1px solid #dfe4ef;
        box-sizing: border-box;

        &[aria-expanded="true"] svg {
          transform: rotate(180deg);
        }

        @media screen and (max-width: 768px) {
          display: flex;
        }
      }
    }

    &-docs-group {
      margin-top: 15px;
      display: flex;
      flex-direction: column;

      @media screen and (max-width: 700px) {
        margin-top: 17px;
      }

      &-title {
        font-weight: 500;
        font-size: 13px;
        line-height: 100%;
        color: #8e9cbb;
        margin-bottom: 14px;
      }

      &-item {
        display: flex;
        align-items: center;
        padding: 8px 12px;
        border-top: solid 1px #dfe4ef;

        @media screen and (max-width: 700px) {
          margin-left: -20px;
          width: calc(100% + 40px);
          padding: 8px 20px;
        }

        &:last-child {
          border-bottom: solid 1px #dfe4ef;
        }

        &-add {
          width: 22px !important;
          height: 22px !important;

          ::v-deep & .c-button-icon {
            width: 10px !important;
            height: 10px !important;
            margin-left: -5px !important;
            margin-top: -5px !important;
          }
        }

        &-name {
          display: flex;
          align-items: center;
          margin-left: 12px;
          font-size: 13px;
          line-height: 120%;
          max-width: 90%;
          display: inline-block;
          overflow-x: hidden;
          text-overflow: ellipsis;
        }
      }
    }
  }

  &-content {
    transition: all 0.4s ease-out;
    max-width: 862px;
    width: 100%;
    margin-left: 30px;

    &-not-need-documents-add {
      transition: all 0.4s ease-out;
      margin-left: 0;
      max-width: 1128px;

      @media screen and (max-width: 1024px) {
        margin: 0 !important;
      }
    }

    @media screen and (max-width: 1024px) {
      margin: 20px 0 0 0;
      max-width: 100%;
    }

    @media screen and (max-width: 768px) {
      margin: 10px 0 0 0;
    }

    &-header {
      display: flex;
      justify-content: space-between;

      @media screen and (max-width: 700px) {
        flex-direction: column;
      }

      &-switcher {
        @media screen and (max-width: 700px) {
          width: 100%;
        }

        ::v-deep & .c-switcher-group {
          @media screen and (max-width: 700px) {
            width: 100%;
          }
        }

        ::v-deep & .c-switcher-group .c-switcher-group-item {
          @media screen and (max-width: 700px) {
            width: 50%;
          }
        }
      }

      &-buttons {
        display: flex;
        align-items: center;
        margin-left: 9px;
        justify-content: flex-end;
        width: 100%;

        @media screen and (max-width: 700px) {
          align-self: flex-end;
        }

        &-download {
          //margin-left: auto;
        }

        &-add-document {
          margin-left: 9px;
        }
      }
    }

    &-table {
      margin-top: 20px;
      margin-bottom: 80px;

      @media screen and (max-width: 700px) {
        margin-top: -4px;
      }

      &-table {
        width: 100%;

        @media screen and (max-width: 700px) {
          width: calc(100% + 40px);
          max-width: calc(100% + 40px);
        }

        ::v-deep .c-table__shadow {
          display: none !important;
        }
      }

      &-loader {
        max-width: 1127px;

        @media screen and (max-width: 700px) {
          margin-top: 20px;

          ::v-deep & .loader-overlay {
            width: calc(100% + 40px);
            left: -20px;
            border-right: none;
            border-left: none;
          }
        }
      }
    }
  }

  &-loader {
    max-width: 1200px;
    width: 100%;

    @media screen and (max-width: 700px) {
      ::v-deep & .loader-overlay {
        width: calc(100% + 40px);
        left: -20px;
        border-right: none;
        border-left: none;
      }
    }
  }
}
.no-required-analyses {
  &-label {
    color: $label;
    font-size: 13px;
    margin-right: auto;
    & span {
      font-weight: 500;
    }
  }
  @media (max-width: 680px) {
    flex-direction: column;
    align-items: flex-start;
    margin-top: 0;
    &-add-button {
      margin-top: 20px;
      margin-left: 0;
    }
  }
}
</style>
